import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { PillTabs } from '../../../components/design-system/admin';
import { PageContainer } from '../../../common/components/PageContainer';
import { RetailerNetworkSummary, useNetworkApi } from '../../../common/hooks/use-network-api.hook';
import { readApiError } from '../../../common/utils/api-error';
import { BrandsGrid } from '../../../modules/vendor/network/BrandsGrid';
import { DataSharingCard } from '../../../modules/vendor/network/DataSharingCard';
import { MatchesPanel } from '../../../modules/vendor/network/MatchesPanel';
import { NetworkHero, ScanUiState } from '../../../modules/vendor/network/NetworkHero';
import { SnapFindCard } from '../../../modules/vendor/network/SnapFindCard';

type Tab = 'matches' | 'browse' | 'sharing' | 'snap';
const TABS: Tab[] = ['matches', 'browse', 'sharing', 'snap'];
const SCAN_POLL_MS = 3000;

/**
 * The Ardealy Network, from the retailer's side: what of their catalogue is
 * already in it, the matches to confirm, the brands they can add from, what
 * they share, and Snap & Find (a shopper's photo → the closest products).
 *
 * Lives with the catalogue (Products → Network) because that is what it
 * changes: every confirmed match and every import is a catalogue product.
 */
export const NetworkPage: React.FC = () => {
    const { t } = useTranslation();
    const api = useNetworkApi();
    const [params, setParams] = useSearchParams();
    const tab = (TABS.includes(params.get('tab') as Tab) ? params.get('tab') : 'matches') as Tab;
    const setTab = (next: Tab) => setParams((p) => {
        const copy = new URLSearchParams(p);
        copy.set('tab', next);
        return copy;
    }, { replace: true });

    const [summary, setSummary] = useState<RetailerNetworkSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [scan, setScan] = useState<ScanUiState>('idle');
    const [matchesKey, setMatchesKey] = useState(0);
    const poll = useRef<number | null>(null);

    const loadSummary = useCallback(async () => {
        try {
            const s = await api.summary();
            setSummary(s);
            return s;
        } catch (e) {
            toast.error((await readApiError(e)).message || t('NETWORK_RETAILER.ERROR'));
            return null;
        } finally {
            setLoading(false);
        }
    }, [api, t]);

    useEffect(() => {
        loadSummary();
        return () => {
            if (poll.current) window.clearTimeout(poll.current);
        };
    }, [loadSummary]);

    // A scan runs in the background: follow it through the summary, then show what it found.
    const follow = useCallback(() => {
        if (poll.current) window.clearTimeout(poll.current);
        poll.current = window.setTimeout(async () => {
            const s = await loadSummary();
            if (s?.lastScan?.status === 'running') {
                follow();
                return;
            }
            setScan('idle');
            setMatchesKey((k) => k + 1);
            if (s?.lastScan?.status === 'done') {
                toast.success(t('NETWORK_RETAILER.SCAN.DONE', { count: (s.lastScan.result?.suggested ?? 0) + (s.lastScan.result?.confirmed ?? 0) }));
            }
        }, SCAN_POLL_MS);
    }, [loadSummary, t]);

    useEffect(() => {
        if (summary?.lastScan?.status === 'running' && scan === 'idle') {
            setScan('running');
            follow();
        }
    }, [summary, scan, follow]);

    const startScan = async () => {
        setScan('starting');
        try {
            await api.scan();
            setScan('running');
            follow();
        } catch (e) {
            const body = await readApiError(e);
            if (body.status === 503) {
                // Not wired on this server yet: said in the hero, not as an error.
                setScan('unavailable');
            } else if (body.status === 409) {
                setScan('running');
                follow();
            } else {
                setScan('idle');
                toast.error(body.message || t('NETWORK_RETAILER.ERROR'));
            }
        }
    };

    const refresh = () => {
        loadSummary();
    };

    return (
        <PageContainer breadcrumbs={[{ label: t('SIDEBAR.PRODUCTS.TITLE') }, { label: t('NETWORK_RETAILER.NAV') }]}>
            <div className="mx-auto flex max-w-[1100px] flex-col gap-6" data-testid="network-page">
                <div>
                    <h1 className="font-heading text-[22px] font-medium tracking-tight">{t('NETWORK_RETAILER.TITLE')}</h1>
                    <p className="mt-1 text-sm text-muted-foreground">{t('NETWORK_RETAILER.SUBTITLE')}</p>
                </div>

                <NetworkHero
                    summary={summary}
                    loading={loading}
                    scan={scan}
                    onScan={startScan}
                    onReviewMatches={() => setTab('matches')}
                    onBrowse={() => setTab('browse')}
                />

                <PillTabs<Tab>
                    value={tab}
                    onChange={setTab}
                    ariaLabel={t('NETWORK_RETAILER.TITLE')}
                    options={[
                        { value: 'matches', label: t('NETWORK_RETAILER.TAB.MATCHES'), count: summary?.suggested },
                        { value: 'browse', label: t('NETWORK_RETAILER.TAB.BROWSE') },
                        { value: 'sharing', label: t('NETWORK_RETAILER.TAB.SHARING') },
                        { value: 'snap', label: t('NETWORK_RETAILER.TAB.SNAP') },
                    ]}
                    className="self-start"
                />

                {tab === 'matches' && (
                    <MatchesPanel key={matchesKey} suggestedCount={summary?.suggested ?? 0} onChanged={refresh} onBrowse={() => setTab('browse')} />
                )}
                {tab === 'browse' && <BrandsGrid />}
                {tab === 'snap' && <SnapFindCard />}
                {tab === 'sharing' && (
                    <DataSharingCard
                        settings={summary?.settings ?? null}
                        onSaved={(settings) => setSummary((s) => (s ? { ...s, settings } : s))}
                    />
                )}
            </div>
        </PageContainer>
    );
};

export default NetworkPage;
